import { useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronsUpDown, LogOut, Settings, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { ROLE_LABELS } from '@cw/shared';
import { Avatar } from '@/design-system/components/avatar';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from '@/design-system/components/menu';
import { api, ApiError } from '@/lib/api';
import { logout, useMe } from '@/lib/session';
import { cn } from '@/lib/utils';

/** Rodapé da sidebar (SPEC §6): avatar, nome, cargo, organização e acesso à conta. */
export function UserMenu({ compact }: { compact: boolean }) {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();

  async function switchOrg(organizationId: string) {
    if (organizationId === me.organization.id) return;
    try {
      await api.post('/account/switch-organization', { organizationId });
      qc.clear();
      navigate('/');
      toast.success('Organização alterada');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível trocar de organização');
    }
  }

  async function signOut() {
    await logout().catch(() => {});
    qc.clear();
    navigate('/login', { replace: true });
  }

  return (
    <Menu>
      <MenuTrigger
        className={cn(
          'flex items-center gap-2.5 rounded-panel text-left transition-colors hover:bg-hover data-[state=open]:bg-hover',
          compact ? 'p-1' : 'w-full p-2',
        )}
        aria-label="Conta e organização"
      >
        <Avatar name={me.user.name} src={me.user.avatarUrl} size={compact ? 'sm' : 'md'} />
        {!compact && (
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13.5px] font-medium">{me.user.name}</span>
            <span className="block truncate text-[12px] text-fg-muted">
              {me.user.title ?? ROLE_LABELS[me.role]}
            </span>
            <span className="block truncate text-[12px] text-fg-subtle">{me.organization.name}</span>
          </span>
        )}
        {!compact && <ChevronsUpDown className="size-4 shrink-0 text-fg-subtle" />}
      </MenuTrigger>
      <MenuContent side={compact ? 'right' : 'top'} align="start" className="w-64">
        <div className="px-2.5 py-2">
          <p className="truncate text-[13.5px] font-medium">{me.user.name}</p>
          <p className="truncate text-[12.5px] text-fg-muted">{me.user.email}</p>
        </div>
        <MenuSeparator />
        {me.memberships.length > 1 && (
          <>
            <MenuLabel>Organização</MenuLabel>
            <MenuRadioGroup value={me.organization.id} onValueChange={switchOrg}>
              {me.memberships.map((m) => (
                <MenuRadioItem key={m.organization.id} value={m.organization.id}>
                  <span className="flex-1 truncate">{m.organization.name}</span>
                  <span className="text-[12px] text-fg-subtle">{ROLE_LABELS[m.role]}</span>
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
            <MenuSeparator />
          </>
        )}
        <MenuItem icon={<UserRound />} onSelect={() => navigate('/settings/account')}>
          Minha conta
        </MenuItem>
        <MenuItem icon={<Building2 />} onSelect={() => navigate('/settings/organization')}>
          {me.organization.name}
        </MenuItem>
        <MenuItem icon={<Settings />} onSelect={() => navigate('/settings')}>
          Configurações
        </MenuItem>
        <MenuSeparator />
        <MenuItem icon={<LogOut />} onSelect={signOut}>
          Sair
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}
